import { useState } from "react";
import { Copy, Check, ArrowSquareOut, Code } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BrandMark, COMPANY_NAME } from "@/components/Brand";

const EMBED_URL = `${window.location.origin}/embed`;

const SNIPPETS = {
  iframe: `<!-- Epoxy Floor Visualizer -->
<iframe
  src="${EMBED_URL}"
  title="Epoxy Floor Visualizer"
  width="100%"
  height="1100"
  style="border:0; border-radius:16px; background:#0a0b0c; max-width:1280px; display:block; margin:0 auto;"
  loading="lazy"
  allow="clipboard-write; web-share"
></iframe>`,
  responsive: `<!-- Epoxy Floor Visualizer (responsive height) -->
<div style="position:relative; width:100%; max-width:1280px; margin:0 auto;">
  <iframe
    src="${EMBED_URL}"
    title="Epoxy Floor Visualizer"
    style="width:100%; height:1100px; border:0; border-radius:16px; background:#0a0b0c;"
    loading="lazy"
    allow="clipboard-write; web-share"
  ></iframe>
</div>
<!-- Optional: auto-resize on mobile -->
<style>
  @media (max-width: 1024px) { iframe[title="Epoxy Floor Visualizer"] { height: 1500px; } }
  @media (max-width: 640px)  { iframe[title="Epoxy Floor Visualizer"] { height: 1800px; } }
</style>`,
  wordpress: `[iframe src="${EMBED_URL}" width="100%" height="1100" frameborder="0" style="border:0;border-radius:16px;background:#0a0b0c;"]`,
  shopify: `<!-- Paste in a "Custom HTML" section -->
<iframe
  src="${EMBED_URL}"
  width="100%"
  height="1100"
  style="border:0;border-radius:16px;background:#0a0b0c;"
  loading="lazy"
></iframe>`,
  squarespace: `<!-- Add a "Code" block and paste -->
<iframe
  src="${EMBED_URL}"
  width="100%"
  height="1100"
  style="border:0;border-radius:16px;background:#0a0b0c;"
></iframe>`,
};

function CodeBlock({ code, name }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };
  return (
    <div
      className="relative rounded-xl border overflow-hidden"
      style={{
        background: "#0b0c0e",
        borderColor: "var(--epx-border)",
      }}
      data-testid={`snippet-${name}`}
    >
      <div
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: "var(--epx-border)" }}
      >
        <p className="font-mono-tech text-xs text-zinc-400">{name}</p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCopy}
          className="h-7 px-2 text-zinc-300 hover:text-white hover:bg-white/5"
          data-testid={`copy-${name}`}
        >
          {copied ? (
            <>
              <Check size={14} className="mr-1.5" /> Copied
            </>
          ) : (
            <>
              <Copy size={14} className="mr-1.5" /> Copy
            </>
          )}
        </Button>
      </div>
      <pre className="p-4 text-xs leading-relaxed overflow-auto font-mono-tech text-zinc-200 max-h-[340px]">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function EmbedGuide() {
  return (
    <div
      className="min-h-screen w-full text-white"
      style={{ background: "var(--epx-bg)" }}
      data-testid="embed-guide-root"
    >
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <BrandMark size="md" />
          <a
            href="/"
            className="text-xs text-zinc-300 hover:text-white font-mono-tech border border-white/10 hover:border-white/30 rounded-md px-3 py-1.5 transition-colors"
            data-testid="back-to-app"
          >
            ← Visualizer
          </a>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              background: "var(--epx-primary)",
              boxShadow: "0 0 20px rgba(var(--epx-primary-rgb),0.45)",
            }}
          >
            <Code size={22} weight="fill" color="#0a0a0a" />
          </div>
          <p className="overline">Embed · {COMPANY_NAME}</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-[1.05] mt-2">
          Add the visualizer to your{" "}
          <span style={{ color: "var(--epx-primary)" }}>website</span>.
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Paste one of these snippets into the HTML of your Epoxy page. The
          tool will appear inline, fully responsive, and styled to match your
          dark/industrial brand.
        </p>

        <div
          className="mt-8 rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
          style={{
            background: "var(--epx-surface)",
            borderColor: "var(--epx-border)",
          }}
        >
          <div className="flex-1 min-w-0">
            <p className="overline">Embed URL</p>
            <p
              className="font-mono-tech text-sm sm:text-base mt-1 truncate"
              data-testid="embed-url"
            >
              {EMBED_URL}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="text-zinc-200 hover:text-white hover:bg-white/5 border border-white/10"
              onClick={() => {
                navigator.clipboard.writeText(EMBED_URL);
                toast.success("URL copied");
              }}
              data-testid="copy-embed-url"
            >
              <Copy size={14} className="mr-1.5" /> Copy URL
            </Button>
            <a
              href={EMBED_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-embed-preview"
            >
              <Button
                className="text-white font-semibold"
                style={{ background: "var(--epx-primary)" }}
              >
                <ArrowSquareOut size={14} className="mr-1.5" /> Preview
              </Button>
            </a>
          </div>
        </div>

        <Section
          title="Standard iframe"
          subtitle="The simplest option. Paste anywhere in your page's HTML."
        >
          <CodeBlock code={SNIPPETS.iframe} name="standard.html" />
        </Section>

        <Section
          title="Responsive iframe"
          subtitle="Auto-adjusts height on tablets and phones so the tool isn't cropped."
        >
          <CodeBlock code={SNIPPETS.responsive} name="responsive.html" />
        </Section>

        <Section
          title="WordPress"
          subtitle="Use this shortcode if you have the iframe plugin, or paste the standard iframe into a Custom HTML block."
        >
          <CodeBlock code={SNIPPETS.wordpress} name="wordpress.txt" />
        </Section>

        <Section
          title="Shopify"
          subtitle="Add a Custom HTML section to your Epoxy product/landing page and paste this."
        >
          <CodeBlock code={SNIPPETS.shopify} name="shopify.html" />
        </Section>

        <Section
          title="Squarespace / Wix / Webflow"
          subtitle="Add a Code / Custom HTML block and paste this."
        >
          <CodeBlock code={SNIPPETS.squarespace} name="custom-html.html" />
        </Section>

        <div
          className="mt-10 rounded-2xl border p-6"
          style={{
            background: "var(--epx-surface)",
            borderColor: "var(--epx-border)",
          }}
        >
          <p className="overline">Quick tips</p>
          <ul className="mt-3 space-y-2 text-sm text-zinc-300 leading-relaxed list-disc pl-5">
            <li>
              The iframe height defaults to <span className="font-mono-tech">1100px</span> — bump it to{" "}
              <span className="font-mono-tech">1500–1800px</span> on mobile so the bottom CTA isn&apos;t cut off.
            </li>
            <li>
              Leads submitted through the quote form land in our database and can be exported anytime — just ask.
            </li>
            <li>
              Want it on a dedicated <span className="font-mono-tech">/visualizer</span> page on your site? Drop the iframe inside any blank page on WordPress / Shopify / Squarespace — no other setup needed.
            </li>
            <li>
              Need it to match a light-themed site? Wrap the iframe in a section with a contrasting background and add{" "}
              <span className="font-mono-tech">box-shadow: 0 24px 60px rgba(0,0,0,0.25)</span> for a premium look.
            </li>
          </ul>
        </div>

        <p className="text-xs text-zinc-500 text-center mt-12">
          That&apos;s it — copy, paste, done.
        </p>
      </main>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-xl sm:text-2xl font-semibold">
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm text-zinc-400 mt-1 mb-4">{subtitle}</p>
      )}
      {children}
    </section>
  );
}
